import React from 'react';
import {
  Box,
  Button,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { IconPlus } from '@tabler/icons';
import { formatDistanceToNow, parseJSON, format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useInternalSupportCases } from '../../../data/support/useSupport';
import { useInternalAccounts } from '../../../data/useInternalAdmin';
import {
  SupportCaseFilters,
  SupportCaseStatus,
  SupportCasePriority,
} from '../../../types/support.types';
import AQDataGrid from '../../AQDataGrid';
import CaseStatusChip from './CaseStatusChip';
import SLAIndicator from './SLAIndicator';
import CreateCaseDialog from './CreateCaseDialog';

const STATUS_OPTIONS: SupportCaseStatus[] = [
  'open',
  'pending',
  'waiting_on_customer',
  'resolved',
  'closed',
];

const PRIORITY_OPTIONS: SupportCasePriority[] = [
  'low',
  'medium',
  'high',
  'critical',
];

export default function InternalSupportCases() {
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = React.useState(false);
  const [statusFilter, setStatusFilter] = React.useState<SupportCaseStatus[]>(
    [],
  );
  const [priorityFilter, setPriorityFilter] = React.useState<
    SupportCasePriority[]
  >([]);
  const [accountFilter, setAccountFilter] = React.useState<string>('');
  const [searchInput, setSearchInput] = React.useState('');
  const [searchDebounced, setSearchDebounced] = React.useState('');
  const debounceRef = React.useRef<ReturnType<typeof setTimeout>>();

  const accountsQuery = useInternalAccounts({ size: 200 });
  const accounts = React.useMemo(() => {
    const data = accountsQuery.data?.data || [];
    return data
      .map((a) => ({ id: a.id, name: a.attributes.title || a.attributes.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [accountsQuery.data]);

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearchDebounced(value), 300);
  };

  React.useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const filters: SupportCaseFilters = React.useMemo(() => {
    const f: SupportCaseFilters = {};
    if (statusFilter.length) f.status = statusFilter;
    if (priorityFilter.length) f.priority = priorityFilter;
    if (accountFilter) f.account_id = accountFilter;
    if (searchDebounced) f.search = searchDebounced;
    return f;
  }, [statusFilter, priorityFilter, accountFilter, searchDebounced]);

  const casesQuery = useInternalSupportCases(filters);
  const cases = casesQuery.data || [];

  const rows = React.useMemo(() => cases.map((c) => ({ ...c })), [cases]);

  const colDefs = React.useMemo(
    () => [
      {
        field: 'case_number',
        headerName: 'Case #',
        width: 100,
        sortable: true,
        filterable: false,
        renderCell: (params: any) => (
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {params.value != null ? `#${params.value}` : '—'}
          </Typography>
        ),
      },
      {
        field: 'subject',
        headerName: 'Subject',
        flex: 1,
        minWidth: 200,
        sortable: true,
        filterable: true,
      },
      {
        field: 'account',
        headerName: 'Account',
        width: 160,
        sortable: false,
        filterable: false,
        renderCell: (params: any) => {
          const account = params.value;
          return (
            <Typography variant="body2">
              {account?.title ||
                account?.name ||
                params.row.account_id?.substring(0, 8) ||
                '—'}
            </Typography>
          );
        },
      },
      {
        field: 'creator',
        headerName: 'Creator',
        width: 160,
        sortable: false,
        filterable: false,
        renderCell: (params: any) => {
          const creator = params.value;
          const creatorType = params.row.creator_type;
          return (
            <Typography variant="body2">
              {creator
                ? creator.display_name || creator.email
                : creatorType || '—'}
            </Typography>
          );
        },
      },
      {
        field: 'priority',
        headerName: 'Priority',
        width: 120,
        sortable: true,
        filterable: false,
        renderCell: (params: any) => (
          <CaseStatusChip value={params.value} type="priority" />
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 150,
        sortable: true,
        filterable: false,
        renderCell: (params: any) => (
          <CaseStatusChip value={params.value} type="status" />
        ),
      },
      {
        field: 'assignee',
        headerName: 'Assignee',
        width: 180,
        sortable: false,
        filterable: false,
        renderCell: (params: any) => {
          const assignee = params.value;
          return (
            <Typography
              variant="body2"
              color={assignee ? 'text.primary' : 'text.disabled'}
            >
              {assignee
                ? assignee.display_name || assignee.email
                : 'Unassigned'}
            </Typography>
          );
        },
      },
      {
        field: '_sla',
        headerName: 'SLA',
        width: 140,
        sortable: false,
        filterable: false,
        renderCell: (params: any) => (
          <SLAIndicator
            createdAt={params.row.created_at}
            status={params.row.status}
            priority={params.row.priority}
          />
        ),
      },
      {
        field: 'created_at',
        headerName: 'Created',
        width: 160,
        sortable: true,
        filterable: false,
        renderCell: (params: any) => (
          <Tooltip
            title={format(parseJSON(params.value), 'PPpp')}
            placement="top"
          >
            <Typography variant="body2" color="text.secondary">
              {formatDistanceToNow(parseJSON(params.value), {
                addSuffix: true,
              })}
            </Typography>
          </Tooltip>
        ),
      },
    ],
    [],
  );

  const gridProps = React.useMemo(
    () => ({
      rowHeight: 52,
      sortingMode: 'client' as const,
      filterMode: 'client' as const,
      paginationMode: 'client' as const,
      disableColumnMenu: true,
      disableRowSelectionOnClick: true,
      initialState: {
        sorting: {
          sortModel: [{ field: 'created_at', sort: 'desc' as const }],
        },
      },
      onRowClick: (params: any) => {
        navigate(`/internal/support/cases/${params.id}`);
      },
      sx: {
        cursor: 'pointer',
        '& .MuiDataGrid-cell': {
          display: 'flex',
          alignItems: 'center',
        },
      },
    }),
    [navigate],
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
          maxWidth: 1200,
          margin: '0 auto 24px',
        }}
      >
        <Typography variant="h5">Support Cases</Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<IconPlus size={16} />}
          onClick={() => setCreateOpen(true)}
        >
          New Case
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          label="Status"
          size="small"
          select
          SelectProps={{
            multiple: true,
            renderValue: (selected: any) =>
              (selected as SupportCaseStatus[]).map((s) => (
                <CaseStatusChip key={s} value={s} type="status" />
              )),
          }}
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(e.target.value as unknown as SupportCaseStatus[])
          }
          sx={{ minWidth: 180 }}
        >
          {STATUS_OPTIONS.map((s) => (
            <MenuItem key={s} value={s}>
              <CaseStatusChip value={s} type="status" />
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Priority"
          size="small"
          select
          SelectProps={{
            multiple: true,
            renderValue: (selected: any) =>
              (selected as SupportCasePriority[]).map((p) => (
                <CaseStatusChip key={p} value={p} type="priority" />
              )),
          }}
          value={priorityFilter}
          onChange={(e) =>
            setPriorityFilter(
              e.target.value as unknown as SupportCasePriority[],
            )
          }
          sx={{ minWidth: 160 }}
        >
          {PRIORITY_OPTIONS.map((p) => (
            <MenuItem key={p} value={p}>
              <CaseStatusChip value={p} type="priority" />
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Account"
          size="small"
          select
          value={accountFilter}
          onChange={(e) => setAccountFilter(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All Accounts</MenuItem>
          {accounts.map((a) => (
            <MenuItem key={a.id} value={a.id}>
              {a.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Search"
          size="small"
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
          sx={{ minWidth: 200 }}
        />
      </Box>

      <AQDataGrid
        id="internal-support-cases"
        rows={rows}
        colDefs={colDefs}
        loading={casesQuery.isLoading}
        gridProps={gridProps}
        customErrorElement={
          casesQuery.isError ? (
            <Typography color="error" sx={{ p: 2, textAlign: 'center' }}>
              Failed to load support cases
            </Typography>
          ) : undefined
        }
      />

      <CreateCaseDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </Box>
  );
}
